;!function(){try{var e="undefined"!=typeof window?window:"undefined"!=typeof global?global:"undefined"!=typeof globalThis?globalThis:"undefined"!=typeof self?self:{};e._sentryModuleMetadata=e._sentryModuleMetadata||{},e._sentryModuleMetadata[(new e.Error).stack]=Object.assign({},e._sentryModuleMetadata[(new e.Error).stack],{"_sentryBundlerPluginAppKey:propstrata-app":true});}catch(e){}}();export type PublicApiErrorPayload = {
	error?: string;
	message?: string;
	requiresLogin?: boolean;
	requiresOTP?: boolean;
};

export class PublicApiError extends Error {
	status: number;
	url?: string;
	payload?: PublicApiErrorPayload;
	rawText?: string;
	requiresLogin: boolean;
	requiresOTP: boolean;

	constructor({
		message,
		payload,
		rawText,
		status,
		url,
	}: {
		message: string;
		status: number;
		url?: string;
		payload?: PublicApiErrorPayload;
		rawText?: string;
	}) {
		super(message);
		this.name = "PublicApiError";
		this.status = status;
		this.url = url;
		this.payload = payload;
		this.rawText = rawText;
		this.requiresLogin = payload?.requiresLogin ?? false;
		this.requiresOTP = payload?.requiresOTP ?? false;
	}
}

function looksLikeJson(text: string): boolean {
	const trimmed = text.trim();
	return trimmed.startsWith("{") || trimmed.startsWith("[");
}

function looksLikeHtml(text: string): boolean {
	const trimmed = text.trim().toLowerCase();
	return (
		trimmed.startsWith("<!doctype") ||
		trimmed.startsWith("<html") ||
		trimmed.startsWith("<head") ||
		trimmed.startsWith("<body")
	);
}

function defaultUserMessageFromStatus(status: number): string {
	if (status === 401)
		return "You’re not authenticated. Please log in and try again.";
	if (status === 403) return "You don’t have permission to view this.";
	if (status === 404)
		return "This shared view can’t be found. The link may be invalid or expired.";
	if (status >= 500)
		return "We’re having trouble loading this right now. Please try again.";
	return "We couldn’t load this. Please try again.";
}

/**
 * Extended options for public API fetches
 */
export interface PublicFetchOptions extends Omit<RequestInit, "headers"> {
	headers?: HeadersInit;
	/** Session token for RESTRICTED shared views (passed via X-Shared-View-Session header) */
	sessionToken?: string;
	/** Email for RESTRICTED shared views (passed via X-Shared-View-Email header to avoid URL logging) */
	email?: string;
}

/**
 * Fetch JSON from a public/shared-view endpoint safely.
 *
 * - Never throws raw JSON parse errors (e.g. when server returns HTML).
 * - Normalizes common shared-view auth flags (requiresLogin/requiresOTP).
 * - Supports sessionToken for third-party embed contexts where cookies don't work.
 */
export async function fetchPublicJson<T>(
	url: string,
	init?: PublicFetchOptions,
): Promise<T> {
	const headers = new Headers(init?.headers ?? {});

	if (!headers.has("Content-Type") && init?.body) {
		headers.set("Content-Type", "application/json");
	}

	// Add session token header for RESTRICTED shared views
	if (init?.sessionToken) {
		headers.set("X-Shared-View-Session", init.sessionToken);
	}

	// Send email via header instead of query param to keep PII out of server logs
	if (init?.email) {
		headers.set("X-Shared-View-Email", init.email);
	}

	// Don't use credentials: "include" by default - third-party cookies are often blocked.
	// Session token header is the primary auth mechanism for embeds.
	const res = await fetch(url, {
		...init,
		headers,
	});

	const text = await res.text().catch(() => "");
	const contentType = res.headers.get("content-type") ?? "";

	let payload: PublicApiErrorPayload | undefined;
	let data: unknown;

	const shouldTryJson =
		contentType.includes("application/json") || looksLikeJson(text);

	if (shouldTryJson && text.trim()) {
		try {
			data = JSON.parse(text);
			if (data && typeof data === "object" && !Array.isArray(data)) {
				payload = data as PublicApiErrorPayload;
			}
		} catch {
			// Ignore parse failures; we’ll surface a friendly error below.
		}
	}

	// If response is not OK, throw normalized error (use payload if available)
	if (!res.ok) {
		const messageFromPayload =
			(payload?.error && String(payload.error)) ||
			(payload?.message && String(payload.message));
		const message =
			messageFromPayload ?? defaultUserMessageFromStatus(res.status);

		throw new PublicApiError({
			message,
			payload,
			rawText: looksLikeHtml(text) ? text : undefined,
			status: res.status,
			url: res.url || url,
		});
	}

	// OK response, but we still might not have valid JSON (e.g. HTML 404 page routed incorrectly)
	if (data === undefined) {
		throw new PublicApiError({
			message: looksLikeHtml(text)
				? "We received an unexpected response while loading this shared view. Please try again."
				: "We couldn’t parse the server response. Please try again.",
			payload,
			rawText: looksLikeHtml(text) ? text : undefined,
			status: res.status,
			url: res.url || url,
		});
	}

	return data as T;
}
